import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useTheme } from '../contexts/ThemeContext';

const Services = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef(null);
  const { isDark } = useTheme();
  const [expandedCards, setExpandedCards] = useState(new Set());

  const toggleCard = (index) => {
    const newExpandedCards = new Set(expandedCards);
    if (newExpandedCards.has(index)) {
      newExpandedCards.delete(index);
    } else {
      newExpandedCards.add(index);
    }
    setExpandedCards(newExpandedCards);
  };

  useEffect(() => {
    const cardsContainer = cardsRef.current;
    if (!cardsContainer) return;

    const cards = Array.from(cardsContainer.children);
    if (cards.length === 0) return;

    // Enhanced stagger animation for service cards
    gsap.fromTo(cards,
      { y: 100, opacity: 0, scale: 0.5 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.2,
        stagger: 0.15,
        ease: "power4.out",
        // scrollTrigger: {
        //   trigger: cardsContainer,
        //   start: "top 80%",
        //   end: "bottom 20%",
        //   toggleActions: "play none none reverse"
        // }
      }
    );

    // No hover animations - icons remain static

  }, []);

  const services = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      title: "Web Development",
      description: "Custom websites and web applications built with modern technologies and best practices for optimal performance and user experience.",
      features: ["React & Next.js", "Node.js Backend", "Database Design", "API Integration"]
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: "App Development",
      description: "Native and cross-platform mobile applications that deliver exceptional user experiences across iOS and Android devices.",
      features: ["React Native", "Flutter", "iOS & Android", "App Store Deployment"]
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
        </svg>
      ),
      title: "UI/UX Design",
      description: "Beautiful, intuitive user interfaces and experiences that engage users and drive conversions through thoughtful design.",
      features: ["User Research", "Wireframing", "Prototyping", "Design Systems"]
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: "IT Consulting",
      description: "Strategic technology consulting to help businesses optimize their IT infrastructure and digital transformation initiatives.",
      features: ["Technology Strategy", "Digital Transformation", "Process Optimization", "Security Audits"]
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
      title: "Cloud Solutions",
      description: "Scalable cloud infrastructure and migration services to modernize your business operations and reduce costs.",
      features: ["AWS & Azure", "Cloud Migration", "DevOps", "Infrastructure as Code"]
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Quality Assurance",
      description: "Comprehensive testing and quality assurance services to ensure your applications are robust, reliable, and perform flawlessly.",
      features: ["Automated Testing", "Performance Testing", "Security Testing", "Bug Fixing"]
    }
  ];

  return (
    <section id="services" ref={sectionRef} className="pb-14 pt-8 bg-white dark:bg-gray-900 relative">
      {/* Section Separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-primary-600 to-transparent"></div>
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
            Our <span className="text-[#0284c7]">Services</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            We offer comprehensive technology solutions to help your business thrive in the digital age. 
            From web development to cloud solutions, we've got you covered.
          </p>
        </div>

        {/* Services List Layout */}
        <div ref={cardsRef} className="space-y-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border-l-4 border-[#0284c7] hover:border-l-8"
            >
              {/* Desktop Layout - Always show full content */}
              <div className="hidden lg:flex lg:flex-row lg:items-center gap-8">
                {/* Icon and Title */}
                <div className="flex items-center space-x-6 flex-shrink-0">
                  <div className="service-icon w-16 h-16 bg-[#0284c7] rounded-xl flex items-center justify-center text-white">
                    <div className="relative z-10">
                      {service.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-[#0284c7] transition-colors duration-300">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Description and Features */}
                <div className="flex-1">
                  <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Features Grid */}
                  <div className="grid md:grid-cols-2 gap-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-300">
                        <div className="w-2 h-2 bg-[#0284c7] rounded-full mr-3 flex-shrink-0 group-hover:scale-125 transition-transform duration-300"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile Layout - Icon, Title, and Read More button */}
              <div className="lg:hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="service-icon w-12 h-12 bg-[#0284c7] rounded-xl flex items-center justify-center text-white">
                      <div className="relative z-10">
                        {service.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => toggleCard(index)}
                    className="bg-[#0284c7] hover:bg-[#0271a3] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 hover:scale-105"
                  >
                    {expandedCards.has(index) ? 'Less' : 'More'}
                  </button>
                </div>

                {/* Expanded Content - Only show on mobile when expanded */}
                {expandedCards.has(index) && (
                  <div className="mt-6 space-y-4 animate-fade-in">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {service.description}
                    </p>
                    
                    {/* Features Grid */}
                    <div className="grid grid-cols-1 gap-3">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <div className="w-2 h-2 bg-[#0284c7] rounded-full mr-3 flex-shrink-0"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-14">
          <div className="bg-gradient-to-r from-gray-100 to-gray-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-200/50 dark:border-gray-600/50 backdrop-blur-lg">
            <h3 className="text-3xl md:text-4xl font-display font-bold text-gray-900 dark:text-white mb-6">
              Ready to Transform Your Business?
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-10 max-w-4xl mx-auto leading-relaxed">
              Let's discuss how our services can help you achieve your digital goals and drive business growth.
            </p>
            <button 
              onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#0284c7] hover:bg-[#0271a3] text-white text-lg px-12 py-5 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105 group relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <span>Get Started Today</span>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
      {/* Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-primary-600 to-transparent"></div>
    </section>
  );
};

export default Services;